import {
  DEVICE_PLATFORMS,
  DEVICE_PRESENCE_STATES,
  type DevicePlatform,
  type DevicePresenceState,
} from '@/lib/api/types';
import { DEVICE_SORT_KEYS, type DeviceSortKey, type SortDirection } from '@/lib/api/endpoints/devices';

/*
  The device list keeps its entire state in the URL: search text, filters, sort and page.

  That is not tidiness for its own sake. A technician who has narrowed 4,000 endpoints to the
  six offline machines at one site needs to be able to paste that view into a ticket, and the
  browser back button has to undo a filter rather than leave the page. Component state cannot
  do either.

  Two types, deliberately:

  * DeviceSearchUrl is the wire form. Lists are comma joined strings and every field is
    optional, so /devices with nothing applied has a clean address and a shared link contains
    exactly the narrowing the sender applied. It is also what a <Link search={...}> elsewhere
    in the console has to supply, which is why it is small.
  * DeviceSearchParams is the resolved form the page works with: arrays, no optionals,
    defaults filled in.

  validateDeviceSearch runs on untrusted input, because a URL is user input. Every value is
  checked against the known set and anything unrecognised is dropped rather than forwarded to
  the API.
*/

export interface DeviceSearchUrl {
  readonly q?: string;
  /** Comma joined DevicePresenceState values. */
  readonly states?: string;
  /** Comma joined organization ids. */
  readonly orgs?: string;
  /** Comma joined DevicePlatform values. */
  readonly platforms?: string;
  /** Comma joined tag names. */
  readonly tags?: string;
  readonly sort?: DeviceSortKey;
  readonly dir?: SortDirection;
  readonly page?: number;
}

export interface DeviceSearchParams {
  readonly q: string;
  readonly states: readonly DevicePresenceState[];
  readonly orgs: readonly string[];
  readonly platforms: readonly DevicePlatform[];
  readonly tags: readonly string[];
  readonly sort: DeviceSortKey;
  readonly dir: SortDirection;
  readonly page: number;
}

export const DEVICE_PAGE_SIZE = 25;

export const DEFAULT_DEVICE_SEARCH: DeviceSearchParams = {
  q: '',
  states: [],
  orgs: [],
  platforms: [],
  tags: [],
  sort: 'name',
  dir: 'asc',
  page: 1,
};

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function splitList(raw: string): readonly string[] {
  if (raw === '') {
    return [];
  }
  return [...new Set(raw.split(',').map((part) => part.trim()))].filter((part) => part !== '');
}

/** Filters a comma joined list against an allow list and re-joins it. */
function sanitiseEnumList<T extends string>(raw: string, allowed: readonly T[]): string {
  const allowedSet = new Set<string>(allowed);
  return splitList(raw)
    .filter((part) => allowedSet.has(part))
    .join(',');
}

/** Free-form list (tags, organization ids). Capped so a URL cannot be weaponised. */
function sanitiseFreeList(raw: string, maxItems = 25): string {
  return splitList(raw)
    .filter((part) => part.length <= 120)
    .slice(0, maxItems)
    .join(',');
}

export function validateDeviceSearch(input: Record<string, unknown>): DeviceSearchUrl {
  const out: {
    q?: string;
    states?: string;
    orgs?: string;
    platforms?: string;
    tags?: string;
    sort?: DeviceSortKey;
    dir?: SortDirection;
    page?: number;
  } = {};

  const q = asString(input.q).slice(0, 200);
  if (q !== '') {
    out.q = q;
  }

  const states = sanitiseEnumList(asString(input.states), DEVICE_PRESENCE_STATES);
  if (states !== '') {
    out.states = states;
  }

  const orgs = sanitiseFreeList(asString(input.orgs));
  if (orgs !== '') {
    out.orgs = orgs;
  }

  const platforms = sanitiseEnumList(asString(input.platforms), DEVICE_PLATFORMS);
  if (platforms !== '') {
    out.platforms = platforms;
  }

  const tags = sanitiseFreeList(asString(input.tags));
  if (tags !== '') {
    out.tags = tags;
  }

  const sortCandidate = asString(input.sort);
  if ((DEVICE_SORT_KEYS as readonly string[]).includes(sortCandidate)) {
    out.sort = sortCandidate as DeviceSortKey;
  }

  if (asString(input.dir) === 'desc') {
    out.dir = 'desc';
  }

  const pageRaw = Number(input.page);
  if (Number.isInteger(pageRaw) && pageRaw > 1) {
    out.page = Math.min(pageRaw, 10_000);
  }

  return out;
}

/** Fills defaults and turns the comma joined lists back into arrays. */
export function resolveDeviceSearch(url: DeviceSearchUrl): DeviceSearchParams {
  return {
    q: url.q ?? '',
    states: splitList(url.states ?? '') as readonly DevicePresenceState[],
    orgs: splitList(url.orgs ?? ''),
    platforms: splitList(url.platforms ?? '') as readonly DevicePlatform[],
    tags: splitList(url.tags ?? ''),
    sort: url.sort ?? DEFAULT_DEVICE_SEARCH.sort,
    dir: url.dir ?? DEFAULT_DEVICE_SEARCH.dir,
    page: url.page ?? 1,
  };
}

/** Only non-default values reach the URL. */
export function toUrlSearch(params: DeviceSearchParams): DeviceSearchUrl {
  const out: {
    q?: string;
    states?: string;
    orgs?: string;
    platforms?: string;
    tags?: string;
    sort?: DeviceSortKey;
    dir?: SortDirection;
    page?: number;
  } = {};
  if (params.q !== '') {
    out.q = params.q;
  }
  if (params.states.length > 0) {
    out.states = params.states.join(',');
  }
  if (params.orgs.length > 0) {
    out.orgs = params.orgs.join(',');
  }
  if (params.platforms.length > 0) {
    out.platforms = params.platforms.join(',');
  }
  if (params.tags.length > 0) {
    out.tags = params.tags.join(',');
  }
  if (params.sort !== DEFAULT_DEVICE_SEARCH.sort) {
    out.sort = params.sort;
  }
  if (params.dir !== DEFAULT_DEVICE_SEARCH.dir) {
    out.dir = params.dir;
  }
  if (params.page !== 1) {
    out.page = params.page;
  }
  return out;
}

export function hasActiveFilters(params: DeviceSearchParams): boolean {
  return (
    params.q !== '' ||
    params.states.length > 0 ||
    params.orgs.length > 0 ||
    params.platforms.length > 0 ||
    params.tags.length > 0
  );
}
